/**
 * The pure half of the modal focus trap (see `Dialog.tsx` for the DOM wiring).
 *
 * Split out for the same reason `features/auth/wipe.ts` is split from
 * `WipeDialog.tsx`: the rule is worth asserting directly, the rendering is
 * Playwright's job.
 */

/** Elements that can hold keyboard focus, in document order. */
export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Where Tab should land to keep focus inside the dialog, or `null` when the
 * browser's own default is already correct (i.e. focus is mid-list and simply
 * moves to the neighbour).
 *
 * Wrapping happens at the edges — Tab past the last element returns to the
 * first, Shift+Tab before the first jumps to the last. An `active` element that
 * is not in the list means focus escaped (or never entered), so it is pulled
 * back to the appropriate end.
 */
export function nextTrappedFocus<T>(items: readonly T[], active: T | null, shiftKey: boolean): T | null {
  const first = items[0];
  const last = items[items.length - 1];
  // Also covers the empty case: with nothing focusable there is nowhere to trap
  // focus. Written as an undefined check rather than `items.length === 0`
  // because the design system compiles under `noUncheckedIndexedAccess`, where
  // an index yields `T | undefined` no matter what a length check said.
  if (first === undefined || last === undefined) return null;

  const index = active === null ? -1 : items.indexOf(active);

  if (index === -1) return shiftKey ? last : first;
  if (shiftKey && active === first) return last;
  if (!shiftKey && active === last) return first;
  return null;
}
